// src/services/lostBookService.js
import { db } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';

// Helper to get user-scoped collection reference
const getUserCollection = (userId, collectionName) => {
  if (!userId) throw new Error('userId is required');
  return collection(db, 'users', userId, collectionName);
};

/**
 * Add books to the lost books reorder list
 * Called when marking a charge as paid
 * @param {string} userId - User ID
 * @param {string} chargeId - Original charge document ID
 * @param {Array} books - Array of {title, barcode, cost}
 * @param {string} patronName - Name of patron who lost books
 * @param {string} patronType - 'student' | 'parent' | 'faculty'
 * @returns {Promise<Array>} - Array of new document IDs
 */
export async function addLostBooksToReorder(userId, chargeId, books, patronName, patronType) {
  const docIds = [];

  for (const book of books) {
    const lostBookData = {
      title: book.title,
      barcode: book.barcode,
      cost: book.cost || 0,
      patronName,
      patronType: patronType || 'unknown',
      dateLost: Timestamp.now(),
      chargeId,
      status: 'pending',
      orderedDate: null,
      receivedDate: null,
      createdAt: Timestamp.now()
    };

    const docRef = await addDoc(getUserCollection(userId, 'lostBooksReorder'), lostBookData);
    docIds.push(docRef.id);
  }

  return docIds;
}

/**
 * Get all lost books pending reorder (status = 'pending')
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of lost book objects
 */
export async function getPendingReorderBooks(userId) {
  const q = query(
    getUserCollection(userId, 'lostBooksReorder'),
    where('status', '==', 'pending'),
    orderBy('dateLost', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    dateLost: doc.data().dateLost?.toDate(),
    orderedDate: doc.data().orderedDate?.toDate(),
    receivedDate: doc.data().receivedDate?.toDate(),
    createdAt: doc.data().createdAt?.toDate()
  }));
}

/**
 * Get all lost books, optionally filtered by status
 * @param {string} userId - User ID
 * @param {string} [status] - Optional status filter: 'pending' | 'ordered' | 'received'
 * @returns {Promise<Array>} - Array of lost book objects
 */
export async function getAllLostBooks(userId, status = null) {
  let q;
  if (status) {
    q = query(
      getUserCollection(userId, 'lostBooksReorder'),
      where('status', '==', status),
      orderBy('dateLost', 'desc')
    );
  } else {
    q = query(
      getUserCollection(userId, 'lostBooksReorder'),
      orderBy('dateLost', 'desc')
    );
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    dateLost: doc.data().dateLost?.toDate(),
    orderedDate: doc.data().orderedDate?.toDate(),
    receivedDate: doc.data().receivedDate?.toDate(),
    createdAt: doc.data().createdAt?.toDate()
  }));
}

/**
 * Mark a lost book as ordered
 * @param {string} userId - User ID
 * @param {string} bookId - Lost book document ID
 */
export async function markBookOrdered(userId, bookId) {
  if (!userId) throw new Error('userId is required');
  const bookRef = doc(db, 'users', userId, 'lostBooksReorder', bookId);
  await updateDoc(bookRef, {
    status: 'ordered',
    orderedDate: Timestamp.now()
  });
}

/**
 * Mark a lost book as received
 * @param {string} userId - User ID
 * @param {string} bookId - Lost book document ID
 */
export async function markBookReceived(userId, bookId) {
  if (!userId) throw new Error('userId is required');
  const bookRef = doc(db, 'users', userId, 'lostBooksReorder', bookId);
  await updateDoc(bookRef, {
    status: 'received',
    receivedDate: Timestamp.now()
  });
}

/**
 * Remove a book from the reorder list
 * @param {string} userId - User ID
 * @param {string} bookId - Lost book document ID
 */
export async function removeLostBook(userId, bookId) {
  if (!userId) throw new Error('userId is required');
  const bookRef = doc(db, 'users', userId, 'lostBooksReorder', bookId);
  await deleteDoc(bookRef);
}

/**
 * Get statistics for lost books
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Stats object
 */
export async function getLostBookStats(userId) {
  const allBooks = await getAllLostBooks(userId);

  const stats = {
    totalPending: 0,
    totalOrdered: 0,
    totalReceived: 0,
    pendingCost: 0,
    orderedCost: 0,
    totalBooks: allBooks.length
  };

  allBooks.forEach(book => {
    const cost = book.cost || 0;
    if (book.status === 'pending') {
      stats.totalPending++;
      stats.pendingCost += cost;
    } else if (book.status === 'ordered') {
      stats.totalOrdered++;
      stats.orderedCost += cost;
    } else if (book.status === 'received') {
      stats.totalReceived++;
    }
  });

  return stats;
}

/**
 * Clear all lost books from the reorder list (end of year cleanup)
 * Optionally filter by status
 * @param {string} userId - User ID
 * @param {string} [status] - Optional status to clear: 'pending' | 'ordered' | 'received' | null (all)
 * @returns {Promise<number>} - Number of books deleted
 */
export async function clearAllLostBooks(userId, status = null) {
  const books = await getAllLostBooks(userId, status);

  for (const book of books) {
    const bookRef = doc(db, 'users', userId, 'lostBooksReorder', book.id);
    await deleteDoc(bookRef);
  }

  return books.length;
}
